// Form primitives: the parts a field is assembled from, for the fields the ready-made
// elements (<i80-text-input>, <i80-select>, ...) don't cover.
//
// Most primitives are one element with one class and are written by hand — a label is
// <label class="i80-form-label">. What needs code is the wiring (the generated id shared
// by label/for and aria-describedby), the error's icon, and a count that follows typing.
// So there are four tags here and nothing else.
import { I80Element, define, h } from "../base.js";
import { errorParts, field } from "../field.js";
import { I80FormGroupBase } from "./form.js";

const CONTROL = "input, select, textarea, [contenteditable]";

// --- Field -----------------------------------------------------------------

/**
 * <i80-form-field label="Cluster name" helper-text="Letters and numbers" required>
 *   <input type="text" name="cluster" class="i80-form-text-input i80-typography-body-200 i80-font-weight-regular">
 * </i80-form-field>
 *
 * The label, helper text and error come from attributes; everything you write inside is
 * the control. The element generates one id and links label/for and aria-describedby to
 * it, so an unusual control gets the same accessibility wiring as the ready-made fields.
 * An <i80-form-error> or <i80-form-character-count> child is moved to its own place.
 */
export class I80FormField extends I80Element {
  static get observedAttributes() {
    return ["label", "helper-text", "error", "invalid", "required", "optional"];
  }

  setup() {
    this._layout = this.choice("layout", ["vertical", "flag"], "vertical");
    const existing = this.part("field");
    let control;
    let wrapper = null;
    if (existing) {
      // A clone of rendered markup: the parts are all there, only the ids need renewing.
      this._count = existing.querySelector("i80-form-character-count");
      this._errorEl = existing.querySelector("i80-form-error");
      control = existing.querySelector('[data-i80-part="control"]');
    } else {
      const nodes = this.takeContent();
      this._count = nodes.find((n) => n.localName === "i80-form-character-count") ?? null;
      this._errorEl = nodes.find((n) => n.localName === "i80-form-error") ?? null;
      wrapper = document.createDocumentFragment();
      wrapper.append(...nodes.filter((n) => n !== this._count && n !== this._errorEl));
      control = wrapper.querySelector(CONTROL) ?? wrapper.firstElementChild;
      if (!control) {
        // Nothing to label yet: hold the place until the control turns up (adopt()).
        control = h("span", { "data-i80-placeholder": "" });
        wrapper.append(control);
        this._placeholder = true;
      }
      control.setAttribute("data-i80-part", "control");
    }
    this._control = control;
    this._render = field(this, control, { layout: this._layout, controlWrapper: wrapper });
  }

  /** The .i80-form-field__control box, which only joins the tree on the first render. */
  get controlBox() {
    return this.part("field").querySelector(":scope > .i80-form-field__control");
  }

  adopt() {
    const box = this.controlBox;
    for (const node of this.takeContent()) {
      if (node.localName === "i80-form-character-count") this._count = node;
      else if (node.localName === "i80-form-error") this._errorEl = node;
      else box.append(node);
    }
    const real = this._placeholder && box.querySelector(CONTROL);
    if (real) {
      // The control arrived late; give it the id the label already points at.
      const id = this._control.id;
      this._control.remove();
      this._placeholder = false;
      real.id = id;
      real.setAttribute("data-i80-part", "control");
      this._control = real;
      this._render = field(this, real, { layout: this._layout });
    }
  }

  update() {
    this._render();
    const root = this.part("field");
    const id = this._control.id;
    const described = [];
    for (const [part, prefix] of [[this._count, "character-count"], [this._errorEl, "error"]]) {
      if (!part) continue;
      part.setAttribute("for", id);
      root.append(part);
      described.push(`${prefix}-${id}`);
    }
    if (!described.length) return;
    const own = (this._control.getAttribute("aria-describedby") ?? "").split(" ").filter(Boolean);
    this._control.setAttribute("aria-describedby", [...new Set([...own, ...described])].join(" "));
    if (this._errorEl) this._control.setAttribute("aria-invalid", "true");
  }

  get control() {
    return this._control;
  }
}

// --- Fieldset ---------------------------------------------------------------

/**
 * <i80-form-fieldset legend="Address" helper-text="Where we send the invoice" required>
 *   <i80-text-input label="Street"></i80-text-input>
 *   <i80-text-input label="City"></i80-text-input>
 * </i80-form-fieldset>
 *
 * The same <fieldset class="i80-form-group"> the checkbox, radio and toggle groups render,
 * under a name that does not claim the fields are of one kind.
 */
export class I80FormFieldset extends I80FormGroupBase {}

// --- Error ------------------------------------------------------------------

/**
 * <i80-form-error for="cluster-name">This value is already taken.</i80-form-error>
 *
 * The error block with its icon. Each <p> inside is one message, so a server can render
 * a list of validation errors; text without elements is a single message.
 */
export class I80FormError extends I80Element {
  static get observedAttributes() {
    return ["for", "message"];
  }

  setup() {
    this._box = this.part("error");
    if (this._box) {
      this._messages = [...this._box.querySelectorAll(".i80-form-error__message")];
      return;
    }
    this._messages = this.messagesFrom(this.takeContent());
    this._box = h("div", { "data-i80-part": "error" });
    this.append(this._box);
  }

  /** <p>/<div> children are one message each; loose text and inline tags are one message. */
  messagesFrom(nodes) {
    const blocks = nodes.filter((n) => n.localName === "p" || n.localName === "div");
    if (blocks.length) return blocks;
    return nodes.length ? [h("p", {}, ...nodes)] : [];
  }

  adopt() {
    this._messages.push(...this.messagesFrom(this.takeContent()));
  }

  update() {
    const id = this.getAttribute("for");
    const contextual = this.closest("i80-form-field")
      ? "i80-form-field__error"
      : this.closest("i80-form-fieldset, i80-checkbox-group, i80-radio-group, i80-toggle-group") ? "i80-form-group__error" : null;
    this._box.className = ["i80-form-error", contextual].filter(Boolean).join(" ");
    if (id) this._box.id = `error-${id}`;
    const message = this.getAttribute("message");
    const messages = this._messages.length ? this._messages : message ? [message] : [];
    this._box.replaceChildren(...errorParts(messages));
  }
}

// --- Character count --------------------------------------------------------

/** "3 characters", "1 character": the noun follows the number, as in the Ember component. */
function plural(count, prefix = "") {
  return `${count}${prefix ? ` ${prefix}` : ""} character${count !== 1 ? "s" : ""}`;
}

function counted(value, min, max) {
  const currentLength = value.length;
  const remaining = max === null ? null : max - currentLength;
  const shortfall = min === null ? null : min - currentLength;
  return { currentLength, minLength: min, maxLength: max, remaining, shortfall };
}

function defaultMessage({ currentLength, minLength, maxLength, remaining, shortfall }) {
  if (minLength && currentLength === 0) return `${plural(minLength)} required`;
  if (minLength && currentLength < minLength) return `${plural(shortfall, "more")} required`;
  if (maxLength && currentLength === 0) return `${plural(maxLength)} allowed`;
  if (maxLength && currentLength <= maxLength) return `${plural(remaining)} remaining`;
  if (maxLength && remaining && currentLength > maxLength) return `Exceeded by ${plural(-remaining)}`;
  return `${plural(currentLength)} entered`;
}

/**
 * <input id="summary" maxlength="80">
 * <i80-form-character-count for="summary" max-length="80"></i80-form-character-count>
 *
 * Counts what the control with that id holds and says how much room is left, updating as
 * the user types (aria-live="polite", so it is announced). Set `message` for your own
 * wording, with {currentLength}, {minLength}, {maxLength}, {remaining} and {shortfall}.
 */
export class I80FormCharacterCount extends I80Element {
  static get observedAttributes() {
    return ["for", "min-length", "max-length", "message", "value"];
  }

  setup() {
    this._box = this.part("count") ?? h("div", { "data-i80-part": "count", "aria-live": "polite" });
    if (!this._box.isConnected) this.append(this._box);
    // The control can be anywhere on the page and may not exist yet, so the page is
    // listened to instead of the control; one listener, whatever order the markup arrives in.
    this._onInput = (event) => {
      if (event.target === this.control) this.update();
    };
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("input", this._onInput, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("input", this._onInput, true);
  }

  get control() {
    const id = this.getAttribute("for");
    return id ? document.getElementById(id) : null;
  }

  get length() {
    return this.value.length;
  }

  get value() {
    return this.getAttribute("value") ?? this.control?.value ?? "";
  }

  update() {
    const number = (name) => {
      const raw = this.getAttribute(name);
      return raw === null || raw === "" ? null : parseInt(raw, 10);
    };
    const counts = counted(this.value, number("min-length"), number("max-length"));
    const id = this.getAttribute("for");
    this._box.className = ["i80-form-character-count", this.closest("i80-form-field") && "i80-form-field__character-count"].filter(Boolean).join(" ");
    if (id) this._box.id = `character-count-${id}`;
    const template = this.getAttribute("message");
    this._box.textContent = template
      ? template.replace(/\{(\w+)\}/g, (whole, key) => (key in counts && counts[key] !== null ? String(counts[key]) : whole))
      : defaultMessage(counts);
  }
}

define("i80-form-field", I80FormField);
define("i80-form-fieldset", I80FormFieldset);
define("i80-form-error", I80FormError);
define("i80-form-character-count", I80FormCharacterCount);
